import { assetRecordPrimaryId } from '../utils/assetRecordPrimaryId';
import { prepareRecordForThumbnailLookup } from './assetImageResolve';

export const RECENTLY_VIEWED_ASSETS_STORAGE_KEY = 'ofrontend.recentlyViewedAssets';
const MAX_STORED = 20;

export type RecentlyViewedAssetEntry = {
  id: string;
  asset: Record<string, unknown>;
  viewedAt: number;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

export function readRecentlyViewedAssets(): RecentlyViewedAssetEntry[] {
  try {
    if (typeof localStorage === 'undefined') {
      return [];
    }
    const raw = localStorage.getItem(RECENTLY_VIEWED_ASSETS_STORAGE_KEY);
    if (!raw?.trim()) {
      return [];
    }
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    const out: RecentlyViewedAssetEntry[] = [];
    for (const item of parsed) {
      if (!isRecord(item)) {
        continue;
      }
      const id = typeof item.id === 'string' ? item.id.trim() : '';
      const viewedAt =
        typeof item.viewedAt === 'number' && Number.isFinite(item.viewedAt) ? item.viewedAt : 0;
      const asset = isRecord(item.asset) ? item.asset : null;
      if (!id || !asset) {
        continue;
      }
      out.push({ id, asset: prepareRecordForThumbnailLookup(asset), viewedAt });
    }
    return out.sort((a, b) => b.viewedAt - a.viewedAt);
  } catch {
    return [];
  }
}

function writeRecentlyViewedAssets(entries: RecentlyViewedAssetEntry[]): void {
  try {
    if (typeof localStorage === 'undefined') {
      return;
    }
    localStorage.setItem(RECENTLY_VIEWED_ASSETS_STORAGE_KEY, JSON.stringify(entries.slice(0, MAX_STORED)));
  } catch {
    /* quota / private mode */
  }
}

export function recordRecentlyViewedAsset(asset: Record<string, unknown>): void {
  const id = assetRecordPrimaryId(asset)?.trim();
  if (!id) {
    return;
  }
  const snapshot = prepareRecordForThumbnailLookup(asset);
  const existing = readRecentlyViewedAssets().filter((entry) => entry.id !== id);
  writeRecentlyViewedAssets([{ id, asset: snapshot, viewedAt: Date.now() }, ...existing]);
}

export function recentlyViewedAssetsFromCatalog(
  catalog: Record<string, unknown>[],
  limit: number,
): Record<string, unknown>[] {
  const byId = new Map<string, Record<string, unknown>>();
  for (const asset of catalog) {
    const id = assetRecordPrimaryId(asset)?.trim();
    if (id) {
      byId.set(id, asset);
    }
  }
  const out: Record<string, unknown>[] = [];
  for (const entry of readRecentlyViewedAssets()) {
    if (out.length >= limit) {
      break;
    }
    const fresh = byId.get(entry.id);
    out.push(fresh ?? entry.asset);
  }
  return out;
}
